import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ShieldCheck, Check, ArrowRight, Terminal, Repeat2, FileCheck2 } from "lucide-react";
import { MarketingNav, MarketingFooter, TrialCta } from "@/components/marketing/MarketingChrome";
import { PricingTable } from "@/components/marketing/PricingTable";
import { MARKETING } from "@/lib/marketingContent";

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`mx-auto max-w-7xl px-6 lg:px-10 ${className}`}>{children}</section>
);

export default function Marketing() {
  return (
    <div className="marketing-root min-h-screen bg-[color:var(--marketing-bg)] text-[color:var(--marketing-text)]">
      <MarketingNav active="Product" />

      {/* Hero */}
      <Section className="py-20 lg:py-28">
        <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="overline-label" data-testid="hero-overline">Project &amp; portfolio governance</p>
            <h1 data-testid="hero-title"
              className="mt-5 font-marketing-display text-4xl font-black leading-none tracking-tight sm:text-5xl lg:text-6xl">
              Your projects. Every one.
              <br />
              <span className="text-[color:var(--marketing-primary)]">Under control.</span>
            </h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-[color:var(--marketing-text-muted)]">
              {MARKETING.sub}
            </p>
            <TrialCta />
            <p className="mt-6 font-marketing-mono text-[11px] uppercase tracking-[0.18em] text-slate-400" data-testid="hero-strapline">
              {MARKETING.strapline}
            </p>
            <div className="mt-12 grid max-w-lg grid-cols-3 gap-6" data-testid="hero-proof">
              {MARKETING.proofPoints.map((p) => (
                <div key={p.label} className="border-t-2 border-slate-900 pt-3">
                  <p className="font-marketing-display text-2xl font-black tracking-tight">{p.value}</p>
                  <p className="mt-1 text-xs text-[color:var(--marketing-text-muted)]">{p.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-[color:var(--marketing-border)] bg-white p-7" data-testid="problem-card">
              <p className="overline-label">The problem</p>
              <p className="mt-4 font-marketing-display text-xl font-bold leading-snug tracking-tight">
                {MARKETING.problem.heading}
              </p>
              <ul className="mt-6 space-y-5">
                {MARKETING.problem.pairs.map((p) => (
                  <li key={p.pain}>
                    <div className="flex gap-2.5 text-sm font-medium text-[color:var(--marketing-text)]">
                      <Check size={15} strokeWidth={2} className="mt-0.5 shrink-0 text-[color:var(--marketing-primary)]" />
                      {p.pain}
                    </div>
                    <p className="mt-1.5 border-l-2 border-[color:var(--marketing-primary)] pl-3 text-sm text-[color:var(--marketing-text-muted)]">
                      <span className="font-semibold text-[color:var(--marketing-text)]">So instead: </span>{p.fix}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Assurance facts band */}
      <div className="border-y border-[color:var(--marketing-border)] bg-white py-8">
        <Section>
          <p className="overline-label text-center">Built for regulated, evidence-driven delivery</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3" data-testid="assurance-band">
            {MARKETING.assurances.map((a) => (
              <span key={a} className="inline-flex items-center gap-2 text-xs font-medium text-[color:var(--marketing-text-muted)]">
                <ShieldCheck size={14} strokeWidth={1.5} className="text-[color:var(--marketing-primary)]" /> {a}
              </span>
            ))}
          </div>
        </Section>
      </div>

      {/* What it does — configurable by sector */}
      <Section id="product" className="py-20 lg:py-28">
        <p className="overline-label">{MARKETING.whatItDoes.overline}</p>
        <h2 className="mt-4 max-w-2xl font-marketing-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          {MARKETING.whatItDoes.heading}
        </h2>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-[color:var(--marketing-text-muted)]" data-testid="what-it-does">
          {MARKETING.whatItDoes.body}
        </p>
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-12" data-testid="feature-grid">
          {MARKETING.sectors.map((f) => {
            const Icon = Icons[f.icon] || Icons.Square;
            return (
              <article key={f.title} data-testid={`sector-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                className={`border border-[color:var(--marketing-border)] bg-white p-8 transition-colors hover:border-slate-900 ${f.span}`}>
                <Icon size={22} strokeWidth={1.5} className="text-[color:var(--marketing-primary)]" />
                <h3 className="mt-5 font-marketing-display text-xl font-semibold tracking-tight">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[color:var(--marketing-text-muted)]">{f.body}</p>
              </article>
            );
          })}
        </div>
      </Section>

      {/* Consistency */}
      <section className="border-t border-[color:var(--marketing-border)] bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="overline-label">{MARKETING.consistency.overline}</p>
              <h2 className="mt-4 font-marketing-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                {MARKETING.consistency.heading}
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-[color:var(--marketing-text-muted)]">
                {MARKETING.consistency.body}
              </p>
              <p className="mt-6 border-l-2 border-[color:var(--marketing-primary)] pl-4 text-sm font-semibold">
                {MARKETING.consistency.footnote}
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2" data-testid="consistency-points">
                {MARKETING.consistency.points.map((p) => (
                  <li key={p} className="flex gap-2.5 border-t border-[color:var(--marketing-border)] pt-4 text-sm text-[color:var(--marketing-text-muted)]">
                    <Repeat2 size={15} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[color:var(--marketing-primary)]" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Defensibility — the X factor */}
      <Section className="py-20 lg:py-28">
        <p className="overline-label">{MARKETING.defensibility.overline}</p>
        <h2 className="mt-4 max-w-3xl font-marketing-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          {MARKETING.defensibility.heading}
        </h2>
        <p className="mt-6 max-w-3xl text-base leading-relaxed text-[color:var(--marketing-text-muted)]">
          {MARKETING.defensibility.body}
        </p>
        <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3" data-testid="defensibility-grid">
          {MARKETING.defensibility.points.map(([title, body]) => (
            <div key={title} className="border-t-2 border-slate-900 pt-4">
              <div className="flex items-center gap-2">
                <FileCheck2 size={15} strokeWidth={1.5} className="text-[color:var(--marketing-primary)]" />
                <h3 className="font-marketing-display text-base font-semibold tracking-tight">{title}</h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-[color:var(--marketing-text-muted)]">{body}</p>
            </div>
          ))}
        </div>
        <p className="mt-12 max-w-2xl border-l-2 border-[color:var(--marketing-primary)] pl-4 font-marketing-display text-lg font-semibold tracking-tight"
          data-testid="defensibility-close">
          {MARKETING.defensibility.close}
        </p>
      </Section>

      {/* Roles */}
      <section className="border-t border-[color:var(--marketing-border)] bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <p className="overline-label">{MARKETING.roles.overline}</p>
          <h2 className="mt-4 font-marketing-display text-2xl font-bold tracking-tight sm:text-3xl">
            {MARKETING.roles.heading}
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4" data-testid="roles-grid">
            {MARKETING.roles.items.map(([role, line]) => (
              <div key={role} className="border-t border-[color:var(--marketing-border)] pt-4">
                <p className="font-marketing-mono text-[11px] uppercase tracking-wider text-slate-400">{role}</p>
                <p className="mt-2 text-sm text-[color:var(--marketing-text)]">{line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security / assurance — inverted */}
      <section id="assurance" className="bg-[color:var(--marketing-inverse-bg)] py-20 text-[color:var(--marketing-inverse-text)] lg:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="overline-label text-slate-400">{MARKETING.security.overline}</p>
              <h2 className="mt-4 font-marketing-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
                {MARKETING.security.title}
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-slate-300">{MARKETING.security.body}</p>
              <p className="mt-6 border-l-2 border-[color:var(--marketing-primary)] pl-4 text-sm font-semibold text-white">
                {MARKETING.security.footnote}
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-slate-700 bg-slate-900/60 p-8" data-testid="assurance-points">
                <div className="flex items-center gap-2 text-slate-400">
                  <Terminal size={14} strokeWidth={1.5} />
                  <span className="font-marketing-mono text-xs uppercase tracking-wider">Enforced server-side</span>
                </div>
                <ul className="mt-6 space-y-4">
                  {MARKETING.security.points.map((p) => (
                    <li key={p} className="flex gap-3 text-sm text-slate-200">
                      <ShieldCheck size={16} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[color:var(--marketing-primary)]" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <Section id="pricing" className="py-20 lg:py-28">
        <div className="text-center">
          <p className="overline-label">Pricing</p>
          <h2 className="mt-4 font-marketing-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Three tiers. No surprises.
          </h2>
        </div>
        <div className="mt-12">
          <PricingTable showMatrix={false} />
        </div>
        <div className="mt-10 text-center">
          <Link to="/pricing" data-testid="see-full-comparison"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--marketing-primary)] transition-colors hover:text-[color:var(--marketing-primary-hover)]">
            See the full capability comparison <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="pb-24">
        <p className="overline-label">Questions</p>
        <h2 className="mt-4 font-marketing-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
          What buyers ask us first
        </h2>
        <div className="mt-10 grid grid-cols-1 gap-x-12 gap-y-8 md:grid-cols-2" data-testid="faq-list">
          {MARKETING.faqs.map((f) => (
            <div key={f.q} className="border-t border-[color:var(--marketing-border)] pt-5">
              <h3 className="font-marketing-display text-base font-semibold tracking-tight md:text-lg">{f.q}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[color:var(--marketing-text-muted)]">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Closing CTA */}
      <section className="border-t border-[color:var(--marketing-border)] bg-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div>
              <h2 className="font-marketing-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="closing-cta-heading">
                {MARKETING.cta.heading}
              </h2>
              <p className="mt-3 max-w-xl text-sm text-[color:var(--marketing-text-muted)]" data-testid="closing-cta-body">
                {MARKETING.cta.body}
              </p>
            </div>
            <TrialCta dense />
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
